import React, { useState } from 'react';
import { motion } from 'framer-motion';

const specs = [
  { label: 'Kjøretøyklasser', value: 'Klasse 1–5 (MC til tung lastebil)', icon: '🚗' },
  { label: 'Takstgrupper', value: 'Lav, normal og høy takst', icon: '💰' },
  { label: 'Passeringsdata', value: 'Oppdateres daglig fra Statens vegvesen', icon: '📡' },
  { label: 'AutoPASS-integrasjon', value: 'Full kompatibilitet med alle brikker', icon: '🔗' },
  { label: 'Rabattberegning', value: 'Automatisk 20% månedlig rabatt', icon: '🏷️' },
  { label: 'Historikk', value: 'Opptil 24 måneders passeringshistorikk', icon: '📊' },
];

const steps = [
  { num: '01', title: 'Velg kjøretøytype', desc: 'Systemet identifiserer riktig takstklasse basert på kjøretøyets vekt og aksler.' },
  { num: '02', title: 'Angi reiserute', desc: 'Algoritmen kartlegger alle bomstasjoner langs valgt rute med sanntidsdata.' },
  { num: '03', title: 'Beregn kostnader', desc: 'Nøyaktig kostnadsoversikt med rabatter, tidstakster og månedlige tak.' },
  { num: '04', title: 'Eksporter rapport', desc: 'Last ned detaljert rapport i PDF eller CSV for regnskap og refusjon.' },
];

const BomkalkulatorSection = () => {
  const [expandedStep, setExpandedStep] = useState(null);

  return (
    <section id="bomkalkulator" style={{ padding: '80px 24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '64px' }}>
        <div style={{ width: '4px', height: '56px', background: '#FF5722', flexShrink: 0, marginTop: '4px' }} />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#FF5722' }}>Tjeneste 01</span>
            <div style={{ width: '60px', height: '1px', background: 'rgba(255,87,34,0.3)' }} />
          </div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '30px', fontWeight: 700, color: 'var(--color-foreground)', letterSpacing: '0.02em', marginBottom: '16px', lineHeight: 1.2 }}>Bomkalkulator</h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '16px', fontWeight: 400, color: 'var(--color-muted-foreground)', lineHeight: 1.7, maxWidth: '560px' }}>
            Norges mest presise bomkalkulator beregner nøyaktige kostnader for alle kjøretøytyper på alle norske veier — med sanntidsdata fra Statens vegvesen.
          </p>
        </div>
      </div>
      {/* Main content grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', alignItems: 'start', marginBottom: '64px' }} className="services-grid">
        {/* Left: Dashboard mockup */}
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', top: '-12px', left: '-12px', right: '12px', bottom: '12px', border: '1px solid rgba(255,87,34,0.12)', borderRadius: '2px', pointerEvents: 'none' }} />
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(224,228,232,0.1)', borderTop: '3px solid #FF5722', borderRadius: '2px', overflow: 'hidden' }}>
            {/* Mock dashboard header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(224,228,232,0.08)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(255,87,34,0.6)' }} />
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(255,87,34,0.3)' }} />
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(255,87,34,0.15)' }} />
              <span style={{ marginLeft: '12px', fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', color: 'var(--color-muted-foreground)', textTransform: 'uppercase' }}>Bomkalkulator — Dashboard</span>
            </div>
            {/* Mock stats */}
            <div style={{ padding: '24px 20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                {[['kr 1 240', 'Denne måneden', '#FF5722'], ['kr 8 760', 'Siste 12 mnd', 'rgba(0,79,152,0.8)'], ['23%', 'Spart med rabatt', '#2E7D32']]?.map(([val, lbl, clr]) => (
                  <div key={lbl} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(224,228,232,0.08)', borderRadius: '2px', padding: '14px 12px' }}>
                    <div style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 700, color: clr, marginBottom: '4px' }}>{val}</div>
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--color-muted-foreground)' }}>{lbl}</div>
                  </div>
                ))}
              </div>
              {/* Mock chart bars */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', marginBottom: '12px' }}>Månedlig oversikt</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '80px' }}>
                  {[40, 65, 45, 80, 55, 70, 90, 60, 75, 50, 85, 100]?.map((h, i) => (
                    <div key={i} style={{ flex: 1, height: `${h}%`, background: i === 11 ? '#FF5722' : 'rgba(255,87,34,0.2)', borderRadius: '2px 2px 0 0', transition: 'height 0.3s ease' }} />
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                  {['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des']?.map(m => (
                    <span key={m} style={{ fontFamily: 'var(--font-body)', fontSize: '9px', color: 'rgba(224,228,232,0.3)' }}>{m}</span>
                  ))}
                </div>
              </div>
              {/* Mock route list */}
              {[['E18 Oslo–Drammen', 'kr 42', '3 passeringer'], ['E6 Gardermoen', 'kr 28', '2 passeringer'], ['Rv4 Gjøvik', 'kr 19', '1 passering']]?.map(([route, cost, passes]) => (
                <div key={route} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(224,228,232,0.05)' }}>
                  <div>
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 500, color: 'var(--color-foreground)' }}>{route}</div>
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: '10px', color: 'var(--color-muted-foreground)' }}>{passes}</div>
                  </div>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 700, color: '#FF5722' }}>{cost}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', fontWeight: 600, color: 'var(--color-foreground)', marginBottom: '28px', letterSpacing: '0.01em' }}>Slik fungerer det</h3>
          {steps?.map((step, i) => (
            <motion.div key={step?.num}
              onClick={() => setExpandedStep(expandedStep === i ? null : i)}
              whileHover={{ x: 4 }}
              style={{ borderLeft: expandedStep === i ? '3px solid #FF5722' : '3px solid rgba(224,228,232,0.1)', paddingLeft: '20px', paddingBottom: '28px', cursor: 'pointer', transition: 'border-color 200ms ease' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: expandedStep === i ? '#FF5722' : 'rgba(255,87,34,0.4)' }}>{step?.num}</span>
                <h4 style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 600, color: 'var(--color-foreground)', margin: 0 }}>{step?.title}</h4>
              </div>
              <motion.p
                initial={false}
                animate={{ height: expandedStep === i ? 'auto' : 0, opacity: expandedStep === i ? 1 : 0 }}
                style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 400, color: 'var(--color-muted-foreground)', lineHeight: 1.6, overflow: 'hidden', margin: 0 }}>
                {step?.desc}
              </motion.p>
            </motion.div>
          ))}
        </div>
      </div>
      {/* Technical specs grid */}
      <div>
        <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', fontWeight: 600, color: 'var(--color-foreground)', marginBottom: '24px', letterSpacing: '0.01em' }}>Tekniske spesifikasjoner</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }} className="specs-grid">
          {specs?.map(spec => (
            <div key={spec?.label} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(224,228,232,0.08)', borderRadius: '2px', padding: '20px' }}>
              <div style={{ fontSize: '24px', marginBottom: '10px' }}>{spec?.icon}</div>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 500, color: 'var(--color-foreground)', marginBottom: '6px' }}>{spec?.label}</div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 400, color: 'var(--color-muted-foreground)', lineHeight: 1.5 }}>{spec?.value}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BomkalkulatorSection;
